import { parseOfficesDisplay, type OfficesDisplay } from "@/lib/officesDisplay";

/**
 * The cloud brokerage label: one rule for "is this organization cloud".
 *
 * Stored as `profile_data.workModel` ("offices" | "cloud"), written by staff
 * only (console-org-edit). A managed office carries its company's value in
 * its EFFECTIVE profile (resolved on the server); the public RPC returns it as
 * `work_model` ("cloud" | "cloud_company_office" | "offices" | "unspecified").
 *
 * Every surface reads workModelView / publicWorkModelView, so the badge, the
 * footprint and (later) Explore cannot drift.
 * Spec: docs/superpowers/specs/2026-10-16-cloud-brokerage-label-design.md §3.4
 */
export type WorkModel = "cloud" | "offices" | "unspecified";

/** Anything but exactly "cloud" or "offices" is unspecified. */
export function parseWorkModel(value: unknown): WorkModel {
  return value === "cloud" || value === "offices" ? value : "unspecified";
}

export type CloudBadge = "Cloud brokerage" | "Part of a cloud brokerage";

/** How the organization is described in Compare's "Work model" row. */
export type WorkModelLabel = "Cloud" | "Office of a cloud brokerage" | "Offices" | "Not specified";

export interface WorkModelView {
  /** The header badge, or null for no badge. */
  badge: CloudBadge | null;
  /** Footprint, chip, Nearest and fit treat the organization as cloud. */
  treatedAsCloud: boolean;
  /** Compare's row value. */
  label: WorkModelLabel;
}

const CLOUD: WorkModelView = { badge: "Cloud brokerage", treatedAsCloud: true, label: "Cloud" };
const CLOUD_COMPANY_OFFICE: WorkModelView = {
  badge: "Part of a cloud brokerage",
  treatedAsCloud: false,
  label: "Office of a cloud brokerage",
};
const OFFICES: WorkModelView = { badge: null, treatedAsCloud: false, label: "Offices" };
const UNSPECIFIED: WorkModelView = { badge: null, treatedAsCloud: false, label: "Not specified" };

/** The §3.4 table, from the EFFECTIVE workModel and `is_managed`. */
export function workModelView(workModel: unknown, isManaged: boolean | null | undefined): WorkModelView {
  const wm = parseWorkModel(workModel);
  if (wm === "cloud") return isManaged === true ? CLOUD_COMPANY_OFFICE : CLOUD;
  if (wm === "offices") return OFFICES;
  return UNSPECIFIED;
}

/** The same view from the public RPC's closed `work_model` value; unknown is unspecified. */
export function publicWorkModelView(workModel: unknown): WorkModelView {
  switch (workModel) {
    case "cloud":
      return CLOUD;
    case "cloud_company_office":
      return CLOUD_COMPANY_OFFICE;
    case "offices":
      return OFFICES;
    default:
      return UNSPECIFIED;
  }
}

/** The footprint display for an already computed view: cloud-treated forces states. */
export function officesDisplayFor(view: WorkModelView, officesDisplay: unknown): OfficesDisplay {
  return view.treatedAsCloud ? "states" : parseOfficesDisplay(officesDisplay);
}

/**
 * A cloud-treated organization shows states only, whatever its officesDisplay;
 * everything else (a managed office of a cloud company included) follows its
 * own officesDisplay (§3.5). The office list itself is never touched.
 */
export function effectiveOfficesDisplay(
  officesDisplay: unknown,
  workModel: unknown,
  isManaged: boolean | null | undefined,
): OfficesDisplay {
  return officesDisplayFor(workModelView(workModel, isManaged), officesDisplay);
}

/** effectiveOfficesDisplay for a public profile, from its `work_model`. */
export function publicEffectiveOfficesDisplay(officesDisplay: unknown, workModel: unknown): OfficesDisplay {
  return officesDisplayFor(publicWorkModelView(workModel), officesDisplay);
}

/** The footprint card's line for a cloud-treated organization. */
export const CLOUD_FOOTPRINT_LINE = "Cloud brokerage: agents licensed in these states can join and work remotely.";

/**
 * Why "Show offices as" cannot be changed right now, if it cannot:
 * - "cloud": the organization is treated as a cloud brokerage, which always
 *   shows states (cloud label spec §3.5);
 * - "unknown": the effective work model has not been read yet, so the editor
 *   cannot tell whether the choice applies.
 * Either way the control is shown disabled and never writes.
 */
export type OfficesDisplayLock = "cloud" | "unknown" | false;

/** The lock for an editor that has (or has not yet) read the effective work model. */
export const officesDisplayLock = (workModel: WorkModelView | null | undefined): OfficesDisplayLock =>
  workModel == null ? "unknown" : workModel.treatedAsCloud ? "cloud" : false;
